import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { CREATIVE_CENTER, FileNames, FileTypes } from 'src/interfaces';

import {
  ISplittedLocationData,
  useGetFullLocationData,
  useGetSplittedLocationData,
} from './useGetLocationData';
import { useGetLocationDataHistories } from './useGetLocationDataHistories';

export const useLocationData = (
  locationCode: string,
  date: string,
  time: string,
  timezone = 'utc',
) => {
  const {
    data: locationDataHistories = {},
    isLoading: isLocationDataHistoriesLoading,
    isFetching: isLocationDataHistoriesFetching,
    isSuccess: isLocationDataHistoriesSuccess,
  } = useGetLocationDataHistories(locationCode, date, !!locationCode && !!date);

  const locationVersionKey =
    Object.keys(locationDataHistories).find(
      (timestamp) =>
        dayjs(timestamp).valueOf() <=
        dayjs(`${date} ${time}`).tz(timezone, true).valueOf(),
    ) || '';

  const locationVersionByDateAndTime =
    locationDataHistories[locationVersionKey];

  const isOldVersion = useMemo(
    () => locationVersionByDateAndTime?.[0]?.type === FileTypes.FULL,
    [locationVersionByDateAndTime],
  );

  const {
    data: fullData,
    isLoading: isFullDataLoading,
    isFetching: isFullDataFetching,
    isSuccess: isFullDataSuccess,
  } = useGetFullLocationData(
    locationCode,
    locationVersionByDateAndTime?.[0]?.version_id,
    !isLocationDataHistoriesLoading &&
      !!locationCode &&
      !!locationVersionByDateAndTime &&
      isOldVersion,
  );

  const {
    data: splitData,
    isLoading: isSplitDataLoading,
    isFetching: isSplitDataFetching,
    isSuccess: isSplitDataSuccess,
  } = useGetSplittedLocationData(
    locationCode,
    locationVersionByDateAndTime,
    !isLocationDataHistoriesLoading &&
      !!locationCode &&
      !!locationVersionByDateAndTime &&
      !isOldVersion,
  );

  useEffect(() => {
    if (isOldVersion) {
      localStorage.setItem(CREATIVE_CENTER, JSON.stringify(fullData));
    } else {
      handleSetSplitData(splitData);
    }

    return () => {
      if (isOldVersion) {
        localStorage.removeItem(CREATIVE_CENTER);
      } else {
        handleRemoveSplitData();
      }
    };
  }, [fullData, splitData, isOldVersion]);

  return {
    locationData: isOldVersion
      ? fullData
      : {
          location: splitData?.location,
          projectConfig: splitData?.configs?.projectConfig,
        },
    isLocationDataLoading: isOldVersion
      ? isFullDataLoading
      : isSplitDataLoading,
    isLocationDataFetching: isOldVersion
      ? isFullDataFetching
      : isSplitDataFetching,
    isLocationDataSuccess: isOldVersion
      ? isFullDataSuccess
      : isSplitDataSuccess,
    locationDataHistories,
    isLocationDataHistoriesFetching,
    isLocationDataHistoriesSuccess,
    locationVersion: locationVersionByDateAndTime,
  };
};

const handleSetSplitData = (splitData: ISplittedLocationData) => {
  localStorage.setItem(FileNames.ASSETS, JSON.stringify(splitData?.assets));
  localStorage.setItem(FileNames.PRODUCTS, JSON.stringify(splitData?.products));
  localStorage.setItem(FileNames.CONTENTS, JSON.stringify(splitData?.contents));
  localStorage.setItem(FileNames.CONFIGS, JSON.stringify(splitData?.configs));
  localStorage.setItem(FileNames.DAYPARTS, JSON.stringify(splitData?.dayparts));
  localStorage.setItem(
    FileNames.OVERRIDES,
    JSON.stringify(splitData?.overrides),
  );
  localStorage.setItem(FileNames.LOCATION, JSON.stringify(splitData?.location));

  splitData?.[FileTypes.COMPONENTS] &&
    localStorage.setItem(
      FileNames.COMPONENTS,
      JSON.stringify(splitData?.components),
    );

  splitData?.[FileTypes.TEMPLATES] &&
    localStorage.setItem(
      FileNames.TEMPLATES,
      JSON.stringify(splitData?.templates),
    );

  splitData?.[FileTypes.TEMPLATE_VARIATIONS] &&
    localStorage.setItem(
      FileNames.TEMPLATE_VARIATIONS,
      JSON.stringify(splitData?.['template-variations']),
    );
};

const handleRemoveSplitData = () => {
  localStorage.removeItem(FileNames.ASSETS);
  localStorage.removeItem(FileNames.PRODUCTS);
  localStorage.removeItem(FileNames.CONTENTS);
  localStorage.removeItem(FileNames.CONFIGS);
  localStorage.removeItem(FileNames.DAYPARTS);
  localStorage.removeItem(FileNames.OVERRIDES);
  localStorage.removeItem(FileNames.LOCATION);
  localStorage.removeItem(FileNames.COMPONENTS);
  localStorage.removeItem(FileNames.TEMPLATES);
  localStorage.removeItem(FileNames.TEMPLATE_VARIATIONS);
};
